import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Zap, Calendar, Clock, MapPin, Search, Users, Trophy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Game {
  id: string;
  game_name?: string;
  game_title?: string;
  game_date: string;
  game_time: string;
  venue_name?: string;
  field_location?: string;
  home_team_label: string;
  away_team_label: string;
  sport_id?: number;
  sport_name?: string;
  home_team?: { team_name: string; };
  away_team?: { team_name: string; };
  venue_address?: string;
  needs_scorekeeper?: boolean;
  source: 'regular' | 'venue';
}

export const ScorekeeperGameRequestInterface = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [filteredGames, setFilteredGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [submittingForGame, setSubmittingForGame] = useState<string | null>(null);
  
  const { user: supabaseUser } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const user = supabaseUser || internalUser;

  useEffect(() => {
    loadAvailableGames();
  }, []);

  useEffect(() => {
    filterGames();
  }, [games, searchTerm]);

  const loadAvailableGames = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Load regular games that need scorekeepers (including pending games)
      const { data: regularGames, error: regularError } = await supabase
        .from('games')
        .select(`
          id,
          game_name,
          game_date,
          game_time,
          venue_name,
          home_team_label,
          away_team_label,
          sport_id,
          venue_address,
          available_sports (sport_name)
        `)
        .gte('game_date', today)
        .in('game_status', ['pending', 'confirmed', 'approved'])
        .order('game_date', { ascending: true });

      // Load venue games that need scorekeepers (including pending games)
      const { data: venueGames, error: venueError } = await supabase
        .from('venue_games')
        .select(`
          id,
          game_title,
          game_date,
          game_time,
          field_location,
          home_team:teams!venue_games_home_team_id_fkey(team_name),
          away_team:teams!venue_games_away_team_id_fkey(team_name)
        `)
        .gte('game_date', today)
        .in('status', ['pending', 'confirmed', 'approved'])
        .order('game_date', { ascending: true });

      if (regularError) throw regularError;
      if (venueError) throw venueError;

      // Format and combine games
      const formattedRegularGames: Game[] = (regularGames || []).map(game => ({
        ...game,
        sport_name: game.available_sports?.sport_name,
        source: 'regular' as const,
        needs_scorekeeper: true
      }));

      const formattedVenueGames: Game[] = (venueGames || []).map(game => ({
        ...game,
        game_name: game.game_title,
        venue_name: 'Venue Game',
        home_team_label: game.home_team?.team_name || 'TBD',
        away_team_label: game.away_team?.team_name || 'TBD',
        source: 'venue' as const,
        needs_scorekeeper: true
      }));

      const allGames = [...formattedRegularGames, ...formattedVenueGames];
      setGames(allGames);
    } catch (error) {
      console.error('Error loading games:', error);
      toast.error('Failed to load available games');
    } finally {
      setLoading(false);
    }
  };

  const filterGames = () => {
    if (!searchTerm.trim()) {
      setFilteredGames(games);
      return;
    }

    const filtered = games.filter(game => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (game.game_name || game.game_title || '').toLowerCase().includes(searchLower) ||
        game.home_team_label.toLowerCase().includes(searchLower) ||
        game.away_team_label.toLowerCase().includes(searchLower) ||
        (game.venue_name || '').toLowerCase().includes(searchLower) ||
        (game.sport_name || '').toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredGames(filtered);
  };

  const applyForScorekeeper = async (game: Game) => {
    if (!user) {
      toast.error('Please log in to apply for scorekeeper positions');
      return;
    }

    setSubmittingForGame(game.id);
    try {
      // Check if already applied
      const { data: existingApplication, error: checkError } = await supabase
        .from('scorekeeper_applications')
        .select('id')
        .eq('game_id', game.id)
        .eq('scorekeeper_id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingApplication) {
        toast.error('You have already applied for this game');
        return;
      }

      // Submit application
      const { error: insertError } = await supabase
        .from('scorekeeper_applications')
        .insert({
          game_id: game.id,
          scorekeeper_id: user.id,
          status: 'pending',
          applied_at: new Date().toISOString(),
          game_source: game.source
        });

      if (insertError) throw insertError;

      toast.success('Successfully applied to scorekeeper this game!');
      
    } catch (error) {
      console.error('Error applying for scorekeeper:', error);
      toast.error('Failed to submit application');
    } finally {
      setSubmittingForGame(null);
    }
  };

  const formatGameDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'EEE, MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatGameTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timeString;
    }
  };

  if (!user) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-6 text-center">
          <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Please log in to apply for scorekeeper positions</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Zap className="w-6 h-6 text-primary" />
            Who Needs a ScoreKeeper?
          </CardTitle>
          <p className="text-muted-foreground">
            Find games that need scorekeepers and apply to officiate
          </p>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Search */}
          <div className="space-y-2">
            <Label htmlFor="search" className="text-foreground">Search Games</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by game, teams, venue, or sport..."
                className="pl-10 bg-background border-border text-foreground"
              />
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          )}

          {/* Games List */}
          {!loading && (
            <div className="space-y-4">
              {filteredGames.length === 0 ? (
                <div className="text-center py-8">
                  <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    {searchTerm ? 'No games found matching your search' : 'No games currently need scorekeepers'}
                  </p>
                </div>
              ) : (
                filteredGames.map((game) => (
                  <Card key={game.id} className="border-border bg-muted/20">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg text-foreground">
                              {game.game_name || game.game_title || 'Game'}
                            </h3>
                            <Badge variant="secondary" className="text-xs">
                              {game.source === 'venue' ? 'Venue Game' : 'Regular Game'}
                            </Badge>
                            {game.sport_name && (
                              <Badge variant="outline" className="text-xs">
                                {game.sport_name}
                              </Badge>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Users className="w-4 h-4" />
                              <span>{game.home_team_label} vs {game.away_team_label}</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatGameDate(game.game_date)}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              <span>{formatGameTime(game.game_time)}</span>
                            </div>
                            {(game.venue_name || game.field_location) && (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                <span>{game.venue_name || game.field_location}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="ml-4">
                          <Button
                            onClick={() => applyForScorekeeper(game)}
                            disabled={submittingForGame === game.id}
                            className="bg-primary text-primary-foreground hover:bg-primary/90"
                          >
                            {submittingForGame === game.id ? 'Applying...' : 'Apply to Scorekeeper'}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
